// frontend/src/utils/siteSearch.js
// Site-wide fuzzy search engine. Indexes pages, info, and dynamically-loaded
// menu items / categories. Tolerant to typos via Levenshtein distance.

// ---------- Static index: pages + info entries ----------
// Each entry: { id, type, title, subtitle, url, keywords[], description }

export const STATIC_INDEX = [
  // ---- Pages ----
  {
    id: 'page-home', type: 'page',
    title: 'Home', subtitle: 'Mountain luxury hospitality',
    url: '/',
    keywords: ['home', 'main', 'landing', 'start', 'welcome', 'hotel', 'restaurant', 'resort'],
    description: 'Sun Crest Resort homepage — overview of dining, rooms and experiences.'
  },
  {
    id: 'page-menu', type: 'page',
    title: 'Menu', subtitle: 'Browse our full menu',
    url: '/menu',
    keywords: ['menu', 'food', 'dishes', 'eat', 'cuisine', 'cards', 'meals', 'order', 'dinner', 'lunch', 'breakfast', 'starters', 'mains', 'desserts'],
    description: 'Explore appetizers, main courses, desserts, beverages and chef specials.'
  },
  {
    id: 'page-reservations', type: 'page',
    title: 'Reservations', subtitle: 'Book a table',
    url: '/reservations',
    keywords: ['reservation', 'reservations', 'book', 'booking', 'table', 'reserve', 'dining', 'seat'],
    description: 'Reserve a table for any occasion. Pick your date, time and party size.'
  },
  {
    id: 'page-gallery', type: 'page',
    title: 'Gallery', subtitle: 'Photos of the resort',
    url: '/gallery',
    keywords: ['gallery', 'photos', 'pictures', 'images', 'view', 'tour', 'see'],
    description: 'Browse photos of our restaurant, rooms and mountain surroundings.'
  },
  {
    id: 'page-about', type: 'page',
    title: 'About', subtitle: 'Our story and team',
    url: '/about',
    keywords: ['about', 'story', 'team', 'history', 'who we are', 'mission', 'values', 'chef', 'staff'],
    description: 'The story behind Sun Crest Resort, our team and our values.'
  },
  {
    id: 'page-contact', type: 'page',
    title: 'Contact', subtitle: 'Get in touch',
    url: '/contact',
    keywords: ['contact', 'phone', 'call', 'email', 'address', 'message', 'reach', 'support', 'help', 'directions', 'map'],
    description: 'Call, email, or send us a message. Find directions and opening hours.'
  },
  {
    id: 'page-favorites', type: 'page',
    title: 'Favorites', subtitle: 'Your saved dishes',
    url: '/favorites',
    keywords: ['favorites', 'favourites', 'saved', 'wishlist', 'liked', 'starred', 'heart'],
    description: 'View dishes you have saved as favorites.'
  },
  {
    id: 'page-cart', type: 'page',
    title: 'Cart', subtitle: 'Your order',
    url: '/cart',
    keywords: ['cart', 'basket', 'bag', 'order', 'checkout', 'buy', 'purchase'],
    description: 'Review the items in your cart and proceed to checkout.'
  },
  {
    id: 'page-bookings', type: 'page',
    title: 'My Bookings', subtitle: 'Your reservations and orders',
    url: '/my-bookings',
    keywords: ['bookings', 'my bookings', 'orders', 'history', 'past orders', 'reservations history', 'previous'],
    description: 'View and manage all your reservations and past orders.',
    requiresAuth: true
  },
  {
    id: 'page-profile', type: 'page',
    title: 'Profile', subtitle: 'Account settings',
    url: '/profile',
    keywords: ['profile', 'account', 'settings', 'me', 'user', 'preferences', 'password', 'change password', 'security', 'mfa', 'two factor', 'email change'],
    description: 'Update your profile, change password, manage security and sessions.',
    requiresAuth: true
  },
  {
    id: 'page-login', type: 'page',
    title: 'Sign In', subtitle: 'Log in to your account',
    url: '/login',
    keywords: ['login', 'log in', 'sign in', 'signin', 'enter', 'access', 'account'],
    description: 'Sign in to your existing account.',
    requiresGuest: true
  },
  {
    id: 'page-register', type: 'page',
    title: 'Create Account', subtitle: 'Sign up for free',
    url: '/register',
    keywords: ['register', 'sign up', 'signup', 'create account', 'new account', 'join'],
    description: 'Create a new account to save favorites, track orders and earn rewards.',
    requiresGuest: true
  },
  {
    id: 'page-forgot', type: 'page',
    title: 'Forgot Password', subtitle: 'Reset your password',
    url: '/forgot-password',
    keywords: ['forgot password', 'reset password', 'recover', 'lost password'],
    description: 'Send a reset link to your email if you forgot your password.',
    requiresGuest: true
  },

  // ---- Info entries (FAQ-style) ----
  {
    id: 'info-hours', type: 'info',
    title: 'Opening Hours', subtitle: 'When we are open',
    url: '/contact',
    keywords: ['hours', 'opening', 'open', 'close', 'closing', 'timing', 'schedule', 'when', 'time', 'today'],
    description: 'Daily 7:00 AM to 11:00 PM. Kitchen last orders at 10:30 PM.'
  },
  {
    id: 'info-location', type: 'info',
    title: 'Location & Directions', subtitle: 'How to find us',
    url: '/contact',
    keywords: ['location', 'address', 'directions', 'map', 'where', 'find', 'parking', 'route', 'gps'],
    description: 'Mountain road, hilltop area. See the contact page for directions and parking info.'
  },
  {
    id: 'info-reservations', type: 'info',
    title: 'How to make a reservation', subtitle: 'Booking guide',
    url: '/reservations',
    keywords: ['how to reserve', 'how do i book', 'how to book', 'reservation help', 'booking help'],
    description: 'Pick a date, time and party size on the reservations page. Confirmation by email.'
  },
  {
    id: 'info-cancel', type: 'info',
    title: 'Cancel a booking', subtitle: 'Cancellation policy',
    url: '/my-bookings',
    keywords: ['cancel', 'cancellation', 'refund', 'change booking', 'modify reservation', 'cancel order'],
    description: 'Reservations can be cancelled up to 24 hours before. Orders can be cancelled while pending or confirmed. Use My Bookings.'
  },
  {
    id: 'info-payment', type: 'info',
    title: 'Payment options', subtitle: 'Accepted methods',
    url: '/contact',
    keywords: ['payment', 'pay', 'credit card', 'debit card', 'cash', 'jazzcash', 'easypaisa', 'methods'],
    description: 'We accept cash, all major debit and credit cards, JazzCash and EasyPaisa.'
  },
  {
    id: 'info-delivery', type: 'info',
    title: 'Delivery & Takeaway', subtitle: 'Order options',
    url: '/menu',
    keywords: ['delivery', 'takeaway', 'take away', 'pickup', 'pick up', 'home delivery', 'door delivery'],
    description: 'Order food for dine-in, takeaway or local delivery from the menu page.'
  },
  {
    id: 'info-dietary', type: 'info',
    title: 'Dietary options', subtitle: 'Vegetarian, vegan, gluten-free',
    url: '/menu',
    keywords: ['vegetarian', 'vegan', 'halal', 'gluten free', 'gluten-free', 'allergy', 'allergies', 'dairy free', 'nuts', 'kosher', 'diet', 'dietary'],
    description: 'Filter the menu by dietary preference: vegetarian, vegan, halal and gluten-free options available.'
  },
  {
    id: 'info-private-events', type: 'info',
    title: 'Private events & catering', subtitle: 'Birthdays, weddings, parties',
    url: '/contact',
    keywords: ['event', 'events', 'private dining', 'wedding', 'birthday', 'anniversary', 'party', 'catering', 'corporate', 'function'],
    description: 'Host private events for any occasion. Contact us to plan your event or catering.'
  },
  {
    id: 'info-rooms', type: 'info',
    title: 'Rooms & Stays', subtitle: 'Resort accommodation',
    url: '/about',
    keywords: ['room', 'rooms', 'stay', 'accommodation', 'hotel', 'overnight', 'lodging', 'suite'],
    description: 'Stay overnight at the resort. See the About page or contact us for room availability.'
  },
  {
    id: 'info-dress-code', type: 'info',
    title: 'Dress code', subtitle: 'What to wear',
    url: '/contact',
    keywords: ['dress code', 'attire', 'what to wear', 'clothes', 'formal', 'casual'],
    description: 'Smart casual is recommended. Formal attire welcomed for evening service.'
  },
  {
    id: 'info-kids', type: 'info',
    title: 'Kids & families', subtitle: 'Family-friendly dining',
    url: '/menu',
    keywords: ['kids', 'children', 'child', 'family', 'high chair', 'kids menu', 'baby'],
    description: 'High chairs available, kid-friendly options on the menu.'
  },
  {
    id: 'info-gift-card', type: 'info',
    title: 'Gift cards', subtitle: 'Give the gift of dining',
    url: '/contact',
    keywords: ['gift card', 'gift voucher', 'voucher', 'present'],
    description: 'Gift cards available — contact us for purchase.'
  },
  {
    id: 'info-careers', type: 'info',
    title: 'Careers & jobs', subtitle: 'Work with us',
    url: '/contact',
    keywords: ['career', 'careers', 'job', 'jobs', 'hiring', 'employment', 'work', 'apply'],
    description: 'Interested in joining our team? Reach out via the contact page.'
  }
];

// ---------- String / token utilities ----------

const norm = (s) => String(s || '').toLowerCase().normalize('NFKD').replace(/[^\p{L}\p{N}\s]/gu, ' ').replace(/\s+/g, ' ').trim();

const tokenize = (s) => norm(s).split(' ').filter(Boolean);

// Levenshtein distance (iterative, two-row). Capped early-exit for performance.
function levenshtein(a, b, max = 4) {
  if (a === b) return 0;
  const al = a.length, bl = b.length;
  if (Math.abs(al - bl) > max) return max + 1;
  if (al === 0) return bl;
  if (bl === 0) return al;

  let prev = new Array(bl + 1);
  let curr = new Array(bl + 1);
  for (let j = 0; j <= bl; j++) prev[j] = j;

  for (let i = 1; i <= al; i++) {
    curr[0] = i;
    let rowMin = curr[0];
    const ai = a.charCodeAt(i - 1);
    for (let j = 1; j <= bl; j++) {
      const cost = ai === b.charCodeAt(j - 1) ? 0 : 1;
      curr[j] = Math.min(
        curr[j - 1] + 1,
        prev[j] + 1,
        prev[j - 1] + cost
      );
      if (curr[j] < rowMin) rowMin = curr[j];
    }
    if (rowMin > max) return max + 1; // early exit
    [prev, curr] = [curr, prev];
  }
  return prev[bl];
}

// Returns true if token b is "similar enough" to token a — substring or low Levenshtein.
// Stopword guard: reject candidate tokens shorter than 3 unless they're an exact match.
function tokenMatches(qTok, candTok) {
  if (!qTok || !candTok) return false;
  if (qTok === candTok) return true;
  // Both tokens must be meaningfully long for fuzzy/substring matching to apply.
  if (candTok.length < 3) return false;
  if (qTok.length < 3) return false;
  if (candTok.includes(qTok)) return true;       // qTok appears inside candTok
  // Reverse-substring is risky (qTok contains candTok) — require length parity to avoid stopword leaks.
  if (qTok.length >= 5 && candTok.length >= 4 && qTok.includes(candTok)) return true;
  // Allow distance scaled to the SHORTER token length so 'i' vs 'vegitarian' never qualifies.
  const minLen = Math.min(qTok.length, candTok.length);
  const allow = minLen <= 4 ? 1 : minLen <= 7 ? 2 : 3;
  if (Math.abs(qTok.length - candTok.length) > allow) return false;
  return levenshtein(qTok, candTok, allow) <= allow;
}

// Tokens that should never contribute to scoring on their own.
const STOPWORDS = new Set([
  'a','an','the','of','to','for','and','or','on','in','at','by','is','it','as','be','do','i','my','me','we','us','you','your','our'
]);
const usefulTokens = (toks) => toks.filter(t => t.length >= 2 && !STOPWORDS.has(t));

// ---------- Scoring ----------

function scoreEntry(entry, query) {
  const qNorm = norm(query);
  if (!qNorm) return 0;

  const title = norm(entry.title);
  const subtitle = norm(entry.subtitle || '');
  const description = norm(entry.description || '');
  const keywords = (entry.keywords || []).map(norm);
  const haystack = [title, subtitle, description, keywords.join(' ')].join(' ');

  let score = 0;

  // Whole-phrase matches
  if (title === qNorm) score += 200;
  if (title.includes(qNorm)) score += 100;
  if (subtitle.includes(qNorm)) score += 60;
  if (description.includes(qNorm)) score += 30;
  if (keywords.some(k => k === qNorm)) score += 80;
  if (keywords.some(k => k.includes(qNorm))) score += 40;

  // Per-token fuzzy
  const qTokens = usefulTokens(tokenize(qNorm));
  const titleToks = usefulTokens(tokenize(title));
  const kwToks = usefulTokens(keywords.flatMap(tokenize));
  const hayToks = usefulTokens(tokenize(haystack));

  let strongMatched = false; // require at least one strong (title/keyword) hit to count description noise
  for (const qt of qTokens) {
    if (qt.length < 2) continue;
    let hit = false;
    // Exact substring on full haystack — bigger qt = more meaningful
    if (qt.length >= 3 && haystack.includes(qt)) { score += 14; hit = true; }
    // Title token match (strongest signal)
    if (titleToks.some(t => tokenMatches(qt, t))) { score += 35; hit = true; strongMatched = true; }
    // Keyword token match
    if (kwToks.some(t => tokenMatches(qt, t))) { score += 22; hit = true; strongMatched = true; }
    // Description-only fuzzy fallback — small boost, only counts if we have any strong match elsewhere
    if (!hit && hayToks.some(t => tokenMatches(qt, t))) score += 4;
  }

  // If nothing actually hit title/keywords, drop the score sharply — description noise alone shouldn't surface a result.
  if (!strongMatched && score < 30) return 0;

  // Slight boost for shorter titles (more specific match)
  if (score > 0 && title.length > 0) {
    score += Math.max(0, 10 - Math.floor(title.length / 5));
  }

  return score;
}

// ---------- Public API ----------

// Build a runtime index by combining the static one with menu items + categories
// supplied by the caller. menuItems should be the array returned from getMenuItems.
export function buildIndex({ menuItems = [], categories = [] } = {}) {
  const items = menuItems.map((m) => {
    const tags = []
      .concat(m.dietary || [])
      .concat(m.tags || [])
      .concat(m.allergens ? [`contains ${(m.allergens || []).join(' ')}`] : [])
      .map(String);
    return {
      id: `dish-${m._id || m.id || m.name}`,
      type: 'dish',
      title: m.name || 'Untitled dish',
      subtitle: m.category || 'Menu',
      url: '/menu',
      price: m.price,
      image: m.image,
      rating: m.rating,
      keywords: [
        m.category,
        m.cuisine,
        ...(m.tags || []),
        ...(m.dietary || []),
        ...(m.ingredients || []),
        ...tags
      ].filter(Boolean).map(String),
      description: m.description || '',
      raw: m
    };
  });

  const cats = categories.map((c) => {
    const name = typeof c === 'string' ? c : (c.name || c.title || '');
    return {
      id: `cat-${name}`,
      type: 'category',
      title: name,
      subtitle: 'Menu category',
      url: `/menu?category=${encodeURIComponent(name)}`,
      keywords: [name, 'category', 'section', 'group'],
      description: `Browse all dishes in ${name}.`
    };
  }).filter(e => e.title);

  return [...STATIC_INDEX, ...cats, ...items];
}

// Run search across the index. Returns scored, sorted results.
export function searchIndex(index, query, { limit = 40, threshold = 12 } = {}) {
  const q = (query || '').trim();
  if (!q) return [];

  const scored = [];
  for (const entry of index) {
    const score = scoreEntry(entry, q);
    if (score >= threshold) scored.push({ entry, score });
  }

  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, limit);
}

// "Did you mean…?" — pulls top-3 most-similar titles when the user got 0 results.
export function suggestSpellings(index, query, { limit = 3 } = {}) {
  const q = norm(query);
  if (!q) return [];
  const candidates = [];
  for (const entry of index) {
    const t = norm(entry.title);
    const d = levenshtein(q, t, Math.max(3, Math.floor(t.length / 2)));
    if (d <= Math.max(3, Math.floor(t.length / 2))) {
      candidates.push({ entry, dist: d });
    }
  }
  candidates.sort((a, b) => a.dist - b.dist);
  return candidates.slice(0, limit).map(c => c.entry);
}

// Group results by type, preserving relative order.
export function groupByType(results) {
  const order = ['page', 'category', 'info', 'dish'];
  const groups = {};
  for (const r of results) {
    const t = r.entry.type || 'other';
    if (!groups[t]) groups[t] = [];
    groups[t].push(r);
  }
  const out = [];
  for (const t of order) {
    if (groups[t]?.length) out.push({ type: t, items: groups[t] });
  }
  for (const t of Object.keys(groups)) {
    if (!order.includes(t)) out.push({ type: t, items: groups[t] });
  }
  return out;
}

export const TYPE_LABELS = {
  page: 'Pages',
  info: 'Info & FAQs',
  category: 'Menu Categories',
  dish: 'Dishes',
  other: 'Other'
};
